import { equals } from 'ramda'
import { clsx } from 'clsx'
import React, { useCallback, memo, useMemo } from 'react'
import { FixedSizeList } from 'react-window'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { VirtualizedListBaseProps } from './virtualized-list.types'
import { virtualizedListDefaultProps } from './constant'
import { nothing } from '$func/utils'
import { Scroller } from '$common/scroller'

const ListContainer = styled(Box)`
  background-color: #fff;

  & > div {
    direction: inherit !important;
    text-align: inherit !important;
  }
`

const DEFAULT_LIST_PADDING = 5
const LOADING_ITEMS_COUNT = 3

export const VirtualizedListBase = React.forwardRef(
  function VirtualizedListBaseComponent<Item>(
    {
      height,
      items = [],
      itemCount,
      ListItem,
      SkeletonItem,
      EmptyStateComponent,
      itemSize,
      loading,
      renderItem,
      onItemsRendered,
      overscanCount,
      onScroll,
      className,
      listPadding = DEFAULT_LIST_PADDING,
      hasError,
      ErrorStateComponent: ErrorComponent,
    }: VirtualizedListBaseProps<Item>,
    ref: React.Ref<any>,
  ) {
    const renderRow = useCallback(
      memo(
        ({
          data,
          index,
          style,
        }: {
          data: Item[]
          index: number
          style: any
        }) => {
          const itemData = data[index]
          const modifiedStyle = { ...style, top: style.top + listPadding }

          if (index < items.length && renderItem)
            return renderItem({ itemData, index, style: modifiedStyle })

          return (
            <div key={index} style={modifiedStyle}>
              {index >= items.length
                ? SkeletonItem && <SkeletonItem />
                : ListItem && <ListItem {...itemData} />}
            </div>
          )
        },
        equals,
      ),
      [items.length, renderItem, ListItem, SkeletonItem, loading],
    )

    const isInEmptyState = useMemo(() => {
      return items.length === 0 && !loading
    }, [items, loading])

    if (hasError && !loading && ErrorComponent)
      return (
        <ListContainer sx={{ height }}>
          <ErrorComponent />
        </ListContainer>
      )

    if (isInEmptyState && EmptyStateComponent)
      return (
        <ListContainer sx={{ height }}>
          <EmptyStateComponent />
        </ListContainer>
      )

    return (
      <ListContainer sx={{ height }}>
        <Scroller style={{ height: '100%' }}>
          {({ scrollableNodeRef, contentNodeRef, scrollableNodeClass }) => (
            <FixedSizeList
              className={clsx(className, scrollableNodeClass)}
              outerRef={scrollableNodeRef}
              innerRef={contentNodeRef}
              onScroll={onScroll}
              ref={ref}
              onItemsRendered={onItemsRendered}
              itemData={items}
              height={height}
              width="100%"
              itemSize={itemSize}
              overscanCount={overscanCount}
              itemCount={itemCount + (loading ? LOADING_ITEMS_COUNT : 0)}
            >
              {renderRow}
            </FixedSizeList>
          )}
        </Scroller>
      </ListContainer>
    )
  },
)

VirtualizedListBase.defaultProps = {
  onItemsRendered: nothing,
  ...virtualizedListDefaultProps,
}
